import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as models from '../models'

import type { State } from './state'

/** Defines selection and inventory actions for knowledge-base management. */
export function getActionsConfig() {
  return {
    clearSelectedFile: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    load: store.actions.request({
      input: store.actions.Data.NONE,
      output:
        store.actions.Data.asIs<ents.core.agent.KnowledgeBase.Management>(),
      reducers: {
        cancellation: ({ state }): State => state,
        error: ({ state, data: { error } }): State => ({
          ...state,
          inventory: { error },
        }),
        request: ({ state }): State => ({
          ...state,
          inventory: { loading: true },
        }),
        success: ({ state, data: { response } }): State => ({
          ...state,
          inventory: { data: response },
        }),
      },
    }),
    loadTenants: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<readonly ents.core.admin.Tenant[]>(),
      reducers: {
        cancellation: ({ state }): State => ({
          ...state,
          tenants: { data: state.tenants.data ?? [] },
        }),
        error: ({ state, data: { error } }): State => ({
          ...state,
          tenants: { error },
        }),
        request: ({ state }): State => ({
          ...state,
          tenants: { loading: true },
        }),
        success: ({ state, data: { response } }): State => ({
          ...state,
          tenants: { data: response },
        }),
      },
    }),
    loadZones: store.actions.request({
      input: store.actions.Data.asIs<{
        tenantId: ents.core.admin.Tenant.Id
      }>(),
      output: store.actions.Data.asIs<readonly ents.core.admin.Zone[]>(),
      reducers: {
        cancellation: ({ state, data: { request } }): State =>
          state.zones?.tenantId === request.tenantId
            ? { ...state, zones: undefined }
            : state,
        error: ({ state, data: { error, request } }): State =>
          state.zones?.tenantId === request.tenantId
            ? {
                ...state,
                zones: {
                  entry: { error },
                  tenantId: request.tenantId,
                },
              }
            : state,
        request: ({ state, data: { tenantId } }): State => ({
          ...state,
          zones: {
            entry: { loading: true },
            tenantId,
          },
        }),
        success: ({ state, data: { request, response } }): State =>
          state.zones?.tenantId === request.tenantId
            ? {
                ...state,
                zones: {
                  entry: { data: response },
                  tenantId: request.tenantId,
                },
              }
            : state,
      },
    }),
    selectAgent: store.actions.standalone({
      data: store.actions.Data.asIs<ents.core.agent.Agent.Id | undefined>(),
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    selectDataSource: store.actions.standalone({
      data: store.actions.Data.asIs<
        ents.core.agent.KnowledgeBase.Management.DataSource | undefined
      >(),
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    selectMode: store.actions.standalone({
      data: store.actions.Data.asIs<models.Selection.Mode>(),
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    selectTargetType: store.actions.standalone({
      data: store.actions.Data.asIs<
        | ents.core.agent.KnowledgeBase.Target.Type.Tenant
        | ents.core.agent.KnowledgeBase.Target.Type.Zone
        | undefined
      >(),
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    selectTenant: store.actions.standalone({
      data: store.actions.Data.asIs<ents.core.admin.Tenant.Id | undefined>(),
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    selectZone: store.actions.standalone({
      data: store.actions.Data.asIs<ents.core.admin.Zone.Id | undefined>(),
      reducer: ({ state }): State => _clearSelectedFile(state),
    }),
    selectFile: store.actions.standalone({
      data: store.actions.Data.asIs<File | undefined>(),
      reducer: ({ state, data: file }): State => {
        if (
          file &&
          file.size >
            ents.core.agent.KnowledgeBase.Management.MAXIMUM_FILE_SIZE_BYTES
        ) {
          return {
            ...state,
            fileInputVersion: state.fileInputVersion + 1,
            selectedFile: {
              error: new Error(
                `The selected file exceeds the ${ents.core.agent.KnowledgeBase.Management.MAXIMUM_FILE_SIZE_BYTES / (1024 * 1024)} MB limit.`,
              ),
            },
          }
        }
        return {
          ...state,
          selectedFile: file ? { data: file } : undefined,
        }
      },
    }),
    upload: store.actions.standalone({
      data: store.actions.Data.NONE,
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}

/** Clears a pending upload and recreates the visible file input. */
function _clearSelectedFile(state: State): State {
  return {
    ...state,
    fileInputVersion: state.fileInputVersion + 1,
    selectedFile: undefined,
  }
}
